import React, { useState, useEffect, useRef } from "react";
import "./QuantumLab.css";
import QubitPanel from "./QubitPanel";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import {
  zeroState,
  H,
  X,
  Z,
  applySingleQubitGate,
  applyCNOT,
  applyCZ,
  measureProbabilities,
  blochVector,
  jointProbabilities,
} from "../../utils/quantum";

import QuantumAccessPopup from "./QuantumAccessPopup";

import QuantumCircuitSimulator from "../Builder/QuantumCircuitSimulator";
// import QuantumCircuitDiagram from "../Builder/QuantumCircuitDiagram"

import QuantumAlgorithmSandbox from "../Sandbox/QuantumAlgorithmSandbox";
import QuantumEntanglementPlayground from "../Playground/QuantumEntanglementPlayground";



// Bloch Sphere Arrow with tip dot
// function BlochArrow({ vec }) {
//   const arrowRef = useRef();
//   const tipRef = useRef();

//   useFrame(() => {
//     if (!vec) return;

//     const mapped = new THREE.Vector3(vec[0], vec[2], -vec[1]).normalize();

//     if (arrowRef.current) {
//       arrowRef.current.setDirection(mapped);
//       arrowRef.current.setLength(1.2);
//     }

//     if (tipRef.current) {
//       tipRef.current.position.set(
//         mapped.x * 1.2,
//         mapped.y * 1.2,
//         mapped.z * 1.2
//       );
//     }
//   });

//   return (
//     <>
//       <primitive
//         ref={arrowRef}
//         object={
//           new THREE.ArrowHelper(
//             new THREE.Vector3(0, 1, 0),
//             new THREE.Vector3(0, 0, 0),
//             1.2,
//             0x00ff00
//           )
//         }
//       />
//       <mesh ref={tipRef}>
//         <sphereGeometry args={[0.05, 16, 16]} />
//         <meshBasicMaterial color="yellow" />
//       </mesh>
//     </>
//   );
// }


function BlochArrow({ vec }) {
  const arrowRef = useRef();
  const tipRef = useRef();

  useFrame(() => {
    if (!vec) return;

    const dir = new THREE.Vector3(vec[0], vec[2], -vec[1]);
    if (dir.length() === 0) return;

    const length = 1.2;
    dir.normalize();

    // Arrow
    if (arrowRef.current) {
      arrowRef.current.setDirection(dir);
      arrowRef.current.setLength(length);
    }

    // Dot at tip
    if (tipRef.current) {
      tipRef.current.position.set(dir.x * length, dir.y * length, dir.z * length);
    }
  });

  // Initialize arrow along +X
  return (
    <>
      <primitive
        ref={arrowRef}
        object={new THREE.ArrowHelper(
          new THREE.Vector3(1, 0, 0),
          new THREE.Vector3(0, 0, 0),
          1,
          0x00ff00
        )}
      />
      <mesh ref={tipRef}>
        <sphereGeometry args={[0.05, 16, 16]} />
        <meshBasicMaterial color="yellow" />
      </mesh>
    </>
  );
}






// 3D Bloch Sphere Canvas
// function BlochCanvas({ bloch }) {
//   if (!bloch) return null;

//   return (
//     <Canvas
//       style={{ width: 200, height: 200, marginTop: 10 }}
//       camera={{ position: [0, 0, 1.7] }}
//     >
//       <ambientLight intensity={0.5} />
//       <pointLight position={[5, 5, 5]} />
//       <mesh>
//         <sphereGeometry args={[1, 32, 32]} />
//         <meshBasicMaterial color="#0ff" wireframe />
//       </mesh>
//       <BlochArrow vec={bloch} />
//       <OrbitControls enableZoom={true} />
//     </Canvas>
//   );
// }

function BlochCanvas({ bloch }) {
  if (!bloch) return null;

  return (
    <Canvas style={{ width: 200, height: 200, marginTop: 10 }} camera={{ position: [0, 0, 1.7] }}>
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 5]} />
      <mesh>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial color="#0ff" wireframe />
      </mesh>
      <BlochArrow vec={bloch} />
      <OrbitControls enableZoom={true} />
    </Canvas>
  );
}



function getThreeQubitBlochVectors(state) {
  const nQubits = 3;
  const vectors = [];

  for (let q = 0; q < nQubits; q++) {
    let a = { re: 0, im: 0 },
      b = { re: 0, im: 0 };

    for (let i = 0; i < state.length; i++) {
      const bit = (i >> (nQubits - q - 1)) & 1;
      if (bit === 0) a.re += state[i].re;
      // simple partial trace
      else b.re += state[i].re;
    }

    vectors.push(blochVector([a, b]));
  }

  return vectors; // [bloch0, bloch1, bloch2]
}

// Helper to get Bloch vectors for each qubit in 2-qubit system
function getTwoQubitBlochVectors(state) {
  if (!state) return [null, null];
  if (state.length === 2) return [blochVector([state[0], state[1]]), null];
  if (state.length === 4) {
    const [a, b, c, d] = state;
    const rho0 = [
      { re: a.re + b.re, im: 0 },
      { re: c.re + d.re, im: 0 },
    ];
    const rho1 = [
      { re: a.re + c.re, im: 0 },
      { re: b.re + d.re, im: 0 },
    ];
    return [blochVector(rho0), blochVector(rho1)];
  }
  return [null, null];
}

export default function QuantumLab() {
  const [nQubits, setN] = useState(3);
  const [state, setState] = useState(zeroState(1));
  const [lastMeasure, setLast] = useState(null);
  const [symbolicState, setSymbolicState] = useState(["|0〉"]);
  const [history, setHistory] = useState([]);
  const [targetQubit, setTargetQubit] = useState(0); // NEW: target selector
  const [controlQubit, setControlQubit] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [phaseZ, setPhaseZ] = useState(0);

  const [queue, setQueue] = useState([]);

  useEffect(() => {
    setState(zeroState(nQubits));
    setLast(null);
    setSymbolicState(nQubits === 1 ? ["|0〉"] : ["|00〉"]);
    setHistory([]);
    setTargetQubit(0);
  }, [nQubits]);

  const probs = measureProbabilities(state);
  const singleBloch = nQubits === 1 ? blochVector([state[0], state[1]]) : null;
  const jointProbs = nQubits === 2 ? jointProbabilities(state) : null;
  const [bloch0, bloch1] =
    nQubits === 2 ? getTwoQubitBlochVectors(state) : [null, null];

  const threeBlochs = nQubits === 3 ? getThreeQubitBlochVectors(state) : null;

  const addHistory = (gate, eq) =>
    setHistory((prev) => [...prev, { gate, eq }]);

  // Updated gate function using targetQubit
  const gate = (g) => {
    let newSymbolic = symbolicState[0];

    if (g === "H") {
      setState(applySingleQubitGate(state, nQubits, targetQubit, H));

      // Update symbolicState
      if (nQubits === 1) {
        if (symbolicState[0] === "|0〉") newSymbolic = "(|0〉 + |1〉)/√2";
        else if (symbolicState[0] === "|1〉") newSymbolic = "(|0〉 - |1〉)/√2";
        else newSymbolic = "|0〉";
        setSymbolicState([newSymbolic]);
        addHistory("H", `${symbolicState[0]} → ${newSymbolic}`);
      }

      if (nQubits === 2) {
        if (symbolicState[0] === "|00〉") {
          newSymbolic =
            targetQubit === 0 ? "(|00〉 + |10〉)/√2" : "(|00〉 + |01〉)/√2";
        }
        setSymbolicState([newSymbolic]);
        addHistory("H", `${symbolicState[0]} → ${newSymbolic}`);
      }
    }

    if (g === "X") {
      setState(applySingleQubitGate(state, nQubits, targetQubit, X));

      if (nQubits === 1) {
        newSymbolic = symbolicState[0] === "|0〉" ? "|1〉" : "|0〉";
        setSymbolicState([newSymbolic]);
        addHistory("X", `X(${symbolicState[0]}) = ${newSymbolic}`);
      }

      if (nQubits === 2) {
        const mapping2Q = {
          "|00〉": targetQubit === 0 ? "|10〉" : "|01〉",
          "|01〉": targetQubit === 0 ? "|11〉" : "|00〉",
          "|10〉": targetQubit === 0 ? "|00〉" : "|11〉",
          "|11〉": targetQubit === 0 ? "|01〉" : "|10〉",
        };
        newSymbolic = mapping2Q[symbolicState[0]] || symbolicState[0];
        setSymbolicState([newSymbolic]);
        addHistory(
          "X",
          `X(q${targetQubit})(${symbolicState[0]}) = ${newSymbolic}`
        );
      }
    }

    if (g === "Z") {
      setState(applySingleQubitGate(state, nQubits, targetQubit, Z));
    
      if (nQubits === 1) {
        // |0> or |1> Bloch vector unchanged
        const newSymbolic = symbolicState[0] === "|1〉" ? "-|1〉" : symbolicState[0];
        setSymbolicState([newSymbolic]);
        addHistory("Z", `Z(${symbolicState[0]}) = ${newSymbolic}`);
      }
    }
    
    

    if (g === "CNOT" && nQubits >= 2) {
      setState(applyCNOT(state, 0, 1));
      if (symbolicState[0] === "(|0〉 + |1〉)/√2 ⊗ |0〉")
        newSymbolic = "(|00〉 + |11〉)/√2";
      else if (symbolicState[0] === "|00〉") newSymbolic = "|00〉";
      else if (symbolicState[0] === "|10〉") newSymbolic = "|11〉";
      setSymbolicState([newSymbolic]);
      addHistory("CNOT", newSymbolic);
    }

    if (g === "CZ" && nQubits >= 2) {
      setState(applyCZ(state, 0, 1));

      // Update symbolicState to show –|11〉
      if (symbolicState[0] === "|11〉") {
        newSymbolic = "-|11〉";
      } else if (symbolicState[0] === "(|00〉 + |11〉)/√2") {
        newSymbolic = "(|00〉 – |11〉)/√2";
      } else {
        newSymbolic = symbolicState[0]; // leave other states unchanged
      }

      setSymbolicState([newSymbolic]);
      addHistory("CZ", newSymbolic);
    }

    if (g === "SWAP" && nQubits >= 2) {
      // Swap qubit states in the full state vector
      setState((prev) => {
        const [a, b, c, d] = prev;
        return [a, c, b, d]; // |01> <-> |10>
      });

      // Update symbolic state for readability
      const mapping = {
        "|00〉": "|00〉",
        "|01〉": "|10〉",
        "|10〉": "|01〉",
        "|11〉": "|11〉",
      };
      newSymbolic = mapping[symbolicState[0]] || symbolicState[0];

      setSymbolicState([newSymbolic]);
      addHistory("SWAP", newSymbolic);
    }

   
    

    setLast(null);
  };

  const measure = () => {
    const ps = measureProbabilities(state);
    const r = Math.random();
    let acc = 0,
      idx = 0;
    for (let i = 0; i < ps.length; i++) {
      acc += ps[i];
      if (r <= acc) {
        idx = i;
        break;
      }
    }
    const ns = new Array(ps.length).fill({ re: 0, im: 0 });
    ns[idx] = { re: 1, im: 0 };
    setState(ns);
    setLast(idx);

    const measured = `|${idx.toString(2).padStart(nQubits, "0")}〉`;
    setSymbolicState([measured]);
    addHistory("(M)", `Collapsed → ${measured}`);
  };

  const enqueueGate = (gate, target, control) => {
    setQueue((prev) => [...prev, { gate, target, control }]);
  };

  return (
    <div className="quantum-lab">
      <div className="controls">
        <label>
          Qubits:
          <select
            value={nQubits}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val > 3) {
                setShowPopup(true); // show the hacker popup
              } else {
                setN(val); // ✅ use your own setter
              }
            }}
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
          </select>
        </label>

        {showPopup && (
          <QuantumAccessPopup onClose={() => setShowPopup(false)} />
        )}

        {nQubits === 2 && (
          <label style={{ marginLeft: "20px" }}>
            Target qubit:
            <select
              value={targetQubit}
              onChange={(e) => setTargetQubit(Number(e.target.value))}
            >
              {Array.from({ length: nQubits }, (_, i) => (
                <option key={i} value={i}>
                  Qubit {i + 1}
                </option>
              ))}
            </select>
          </label>
        )}

        {nQubits === 3 && (
          <div className="qubit-selectors" style={{ marginTop: 20 }}>
            <label style={{ marginRight: 20 }}>
              Control:
              <select
                value={controlQubit}
                onChange={(e) => setControlQubit(Number(e.target.value))}
              >
                {Array.from({ length: nQubits }, (_, i) => (
                  <option key={i} value={i}>
                    Qubit {i + 1}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Target:
              <select
                value={targetQubit}
                onChange={(e) => setTargetQubit(Number(e.target.value))}
              >
                {Array.from({ length: nQubits }, (_, i) => (
                  <option key={i} value={i} disabled={i === controlQubit}>
                    Qubit {i + 1}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="layout">
        {(nQubits === 1 || nQubits === 2) && (
          <QubitPanel
            probs={probs}
            is2Qubit={probs.length === 4}
            nQubits={nQubits}
            onH={() => gate("H")}
            onX={() => gate("X")}
            onZ={() => gate("Z")}
            onCNOT={() => gate("CNOT")}
            onCZ={() => gate("CZ")}
            onSWAP={() => gate("SWAP")}
            onMeasure={measure}
            onReset={() => {
              setState(zeroState(nQubits));
              setSymbolicState(nQubits === 1 ? ["|0〉"] : ["|00〉"]);
              setHistory([]);
              setTargetQubit(0);
            }}
          />
        )}

        {/* --- 3-QUBIT LAYOUT --- */}
        {nQubits === 3 && (
          <div className="three-qubit-layout">
            {/* Row 1 – Gate buttons, centered */}

            <div className="three-qubit-buttons">
              <button className="lab-btn" onClick={() => gate("H")}>
                H
              </button>
              <button className="lab-btn" onClick={() => gate("X")}>
                X
              </button>
              <button className="lab-btn" onClick={() => gate("CNOT")}>
                CNOT
              </button>
              <button className="lab-btn" onClick={() => gate("CZ")}>
                CZ
              </button>
              <button className="lab-btn" onClick={() => gate("SWAP")}>
                SWAP
              </button>
              <button className="lab-btn" onClick={measure}>
                Measure
              </button>
              <button
                className="lab-btn"
                onClick={() => {
                  setState(zeroState(3));
                  setSymbolicState(["|000〉"]);
                  setHistory([]);
                  setTargetQubit(0);
                }}
              >
                Reset
              </button>
            </div>

            {/* Row 2 – All possible 3-bit basis states */}
            {/* Row 2 – All possible 3-bit basis states with probability bars */}
            <div className="probs">
              {measureProbabilities(state).map((p, idx) => (
                <div key={idx} className="prob">
                  {/* bar height proportional to probability */}
                  <div className="bar" style={{ height: `${p * 100}%` }} />
                  <div>|{idx.toString(2).padStart(3, "0")}〉</div>
                  {/* <div>{(p * 100).toFixed(1)}%</div> */}
                </div>
              ))}
            </div>

            <br></br>
            <br></br>
            <br></br>

            {/* Row 3 – Three Bloch spheres */}
            <div
              className="three-blochs"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                marginBottom: 20,
              }}
            >
              {threeBlochs.map((vec, i) => (
                <div key={i} className="visual" style={{ textAlign: "center" }}>
                  <h4>Qubit {i + 1}</h4>
                  <BlochCanvas bloch={vec} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Single Qubit Bloch */}
        {nQubits === 1 && (
          <div className="visual">
            <BlochCanvas bloch={singleBloch}  />
            {lastMeasure !== null && (
              <p className="measure">
                Measured state → <span className="ket">{symbolicState[0]}</span>
              </p>
            )}
          </div>
        )}

        {/* Two Qubit Bloch Spheres */}
        {nQubits === 2 && (
          <div className="visual bloch-panel">
            <div className="two-qubit-blochs">
              <div className="bloch-wrapper">
                <h4>Qubit 1</h4>
                <BlochCanvas bloch={bloch0} />
              </div>
              <div className="bloch-wrapper">
                <h4>Qubit 2</h4>
                <BlochCanvas bloch={bloch1} />
              </div>
            </div>
            {lastMeasure !== null && (
              <p className="measure">
                Measured state → <span className="ket">{symbolicState[0]}</span>
              </p>
            )}
          </div>
        )}

        {/* Two-Qubit Probabilities */}
        {nQubits === 2 && jointProbs && (
          <div className="visual joint-table">
            <br></br>
            <h3>Two-Qubit Probabilities</h3>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>Probability</th>
                </tr>
              </thead>
              <tbody>
                {jointProbs.map((p, idx) => (
                  <tr key={idx}>
                    <td>|{idx.toString(2).padStart(2, "0")}〉</td>
                    <td>{(p * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(nQubits === 1 || nQubits === 2) && (
          <div className="visual history-table">
            <table>
              <thead>
                <tr>
                  <th>Gate</th>
                  <th>Equation</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td>{h.gate}</td>
                    <td>{h.eq}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <br></br>
      <div className="layout">
        {nQubits === 3 && (
          <div className="visual joint-table">
            <h3>Two-Qubit Probabilities</h3>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>Probability</th>
                </tr>
              </thead>
              <tbody>
                {measureProbabilities(state).map((p, idx) => (
                  <tr key={idx}>
                    <td>|{idx.toString(2).padStart(3, "0")}〉</td>
                    <td>{(p * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* {nQubits === 3 && (
          <div className="visual history-table">
            <table>
              <thead>
                <tr>
                  <th>Gate</th>
                  <th>Equation</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td>{h.gate}</td>
                    <td>{h.eq}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )} */}
      </div>

      {/* <QuantumCircuitSimulator maxQubits={10} /> */}

      {/* <QuantumAlgorithmSandbox/> */}

      {/* <QuantumEntanglementPlayground/> */}

      
    </div>
  );
}
